import { useCallback, useEffect, useRef, useState } from 'react';
import { describeError } from '../../../services/errorMessage';
import { fetchMitarbeiter } from '../../../services/sifa/sifaService';
import { ladeKataster } from '../../../services/sifa/katasterService';
import { ladeGbu, ladeGbuListe } from '../../../services/sifa/gbuService';
import type { GbuVoll, Kataster, SifaGbu, SifaMitarbeiterBereich } from '../../../services/sifa/gbuTypes';
import type { SifaMitarbeiter } from '../../../services/sifa/types';
import { LEERES_KATASTER, sortiereGbus } from './gbuModulLogic';

/**
 * Das Laden des GBU-Moduls: Liste der Beurteilungen, Betriebskataster und
 * Mitarbeiterstamm — der einzige Zustand, den der Container haelt.
 *
 * Alle drei in EINEM Hook (Muster useUnterweisungen): die Liste braucht die
 * Bereichsnamen aus dem Kataster, „Betrieb" den Stamm fuer die Zuordnung.
 *
 * Der Mitarbeiterstamm kommt aus derselben Quelle wie bei den
 * Unterweisungen (sifaService.fetchMitarbeiter, ohne zusammengefuehrte
 * Dubletten). Fehlt das Kataster (Nr. 167 nicht gefahren), meldet
 * `kataster.schemaFehlt` das — kein Fehler, sondern ein Hinweis.
 */
export const useGbuModul = (projectId: string | null | undefined) => {
  const [gbus, setGbus] = useState<SifaGbu[]>([]);
  const [kataster, setKataster] = useState<Kataster>(LEERES_KATASTER);
  const [mitarbeiter, setMitarbeiter] = useState<SifaMitarbeiter[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const neuLaden = useCallback(async () => {
    if (!projectId) {
      setGbus([]);
      setKataster(LEERES_KATASTER);
      setMitarbeiter([]);
      setLaden(false);
      return;
    }
    setLaden(true);
    setFehler(null);
    try {
      // allSettled: ein fehlender Mitarbeiterstamm darf die Liste nicht verdecken.
      const [k, g, m] = await Promise.allSettled([
        ladeKataster(projectId),
        ladeGbuListe(projectId),
        fetchMitarbeiter(projectId),
      ]);
      const fehlerTexte: string[] = [];
      if (k.status === 'fulfilled') setKataster(k.value); else fehlerTexte.push(describeError(k.reason));
      if (g.status === 'fulfilled') setGbus(sortiereGbus(g.value)); else fehlerTexte.push(describeError(g.reason));
      // Der Stamm ist eine Zugabe (Zuordnung) — ohne ihn bleibt die Auswahl leer.
      if (m.status === 'fulfilled') setMitarbeiter(m.value.filter((x) => !x.zusammengefuehrtIn));
      setFehler(fehlerTexte.length ? fehlerTexte.join(' ') : null);
    } finally {
      setLaden(false);
    }
  }, [projectId]);

  useEffect(() => { void neuLaden(); }, [neuLaden]);

  /** Nimmt eine geaenderte GBU in die Liste auf — vorhandene ersetzt, neue eingereiht. */
  const uebernehmeGbu = useCallback((g: SifaGbu) => {
    setGbus((prev) => sortiereGbus([...prev.filter((x) => x.id !== g.id), g]));
  }, []);

  const entferneGbu = useCallback((id: string) => {
    setGbus((prev) => prev.filter((x) => x.id !== id));
  }, []);

  /** Ersetzt einen Eintrag einer Kataster-Liste (Speichern) bzw. entfernt ihn (Loeschen). */
  const aendereKataster = useCallback((aendere: (k: Kataster) => Kataster) => {
    setKataster((prev) => aendere(prev));
  }, []);

  /** Die Zuordnungen EINES Bereichs ersetzen (Rueckgabe von setzeBereichsMitarbeiter). */
  const setzeZuordnungen = useCallback((bereichId: string, zuordnungen: SifaMitarbeiterBereich[]) => {
    setKataster((prev) => ({
      ...prev,
      zuordnungen: [...prev.zuordnungen.filter((z) => z.bereichId !== bereichId), ...zuordnungen],
    }));
  }, []);

  return {
    gbus, kataster, mitarbeiter, laden, fehler,
    neuLaden, uebernehmeGbu, entferneGbu, aendereKataster, setzeZuordnungen,
  };
};

/**
 * Eine GBU vollstaendig (Kopf, Gefaehrdungen, Massnahmen, Vorsorge,
 * Fassungen) fuer den Editor. `neuLaden` liefert den frischen Stand auch
 * zurueck — die Freigabe braucht ihn sofort (PDF ablegen), nicht erst nach
 * dem naechsten Zeichnen.
 *
 * Eine spaete Antwort fuer eine inzwischen andere GBU wird verworfen.
 */
export const useGbuVoll = (gbuId: string | null) => {
  const [voll, setVoll] = useState<GbuVoll | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const aktuelleId = useRef<string | null>(gbuId);
  // Vor dem Lade-Effekt: Effekte laufen in Reihenfolge, der Lauf sieht schon die neue Id.
  useEffect(() => { aktuelleId.current = gbuId; }, [gbuId]);

  const neuLaden = useCallback(async (): Promise<GbuVoll | null> => {
    if (!gbuId) { setVoll(null); setLaden(false); return null; }
    setLaden(true);
    setFehler(null);
    try {
      const v = await ladeGbu(gbuId);
      if (aktuelleId.current !== gbuId) return null;
      // Eine spaete Antwort (vor einem eigenen Speichern gelesen) bringt nie einen Kopf mit kleinerer Revision zurueck.
      setVoll((prev) => (prev && prev.gbu.id === v.gbu.id && prev.gbu.revision > v.gbu.revision ? { ...v, gbu: prev.gbu } : v));
      return v;
    } catch (e) {
      if (aktuelleId.current === gbuId) setFehler(describeError(e));
      return null;
    } finally {
      if (aktuelleId.current === gbuId) setLaden(false);
    }
  }, [gbuId]);

  useEffect(() => { void neuLaden(); }, [neuLaden]);

  /** Lokale Aenderung ohne Neuladen (die Antwort des Servers steht schon im Ergebnis des Schreibens). */
  const aendere = useCallback((f: (v: GbuVoll) => GbuVoll) => {
    setVoll((prev) => (prev ? f(prev) : prev));
  }, []);

  return { voll, laden, fehler, neuLaden, aendere };
};
